import React, { useEffect } from 'react';
import { COLOR_PALETTE } from '../../constants';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface ColorPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onColorSelect: (color: string) => void;
  currentColor: string;
}

export function ColorPicker({ isOpen, onClose, onColorSelect, currentColor }: ColorPickerProps) {
  const { t } = useTranslation();

  // ✅ AJOUT : Gestion de la touche Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        // ✅ SÉCURITÉ : Vérifier que onClose existe et gérer les erreurs
        if (typeof onClose === 'function') {
          try {
            onClose();
          } catch (error) {
            // Log l'erreur mais ne pas faire planter le composant
            console.error('Error in onClose callback:', error);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // ✅ AJOUT : Focus management - focus sur le modal à l'ouverture
  useEffect(() => {
    if (isOpen) {
      // Focus sur le premier élément focusable du modal
      const modalContent = document.querySelector('[role="dialog"]') as HTMLElement;
      if (modalContent) {
        modalContent.focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;
  
  const colorNames = Object.keys(COLOR_PALETTE);

  // ✅ AJOUT : Fonction pour générer les labels accessibles
  const getColorLabel = (colorName: string, colorValue: string, toneIndex: number) => {
    const isSelected = currentColor === colorValue;
    const toneName = ['dark', 'medium', 'light', 'lighter'][toneIndex] || `tone ${toneIndex + 1}`;
    return `${colorName} ${toneName} ${colorValue}${isSelected ? ' (selected)' : ''}`;
  };

  // ✅ AJOUT : Fonction pour déterminer si une couleur est sélectionnée
  const isColorSelected = (colorValue: string) => {
    return currentColor === colorValue;
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="bg-white p-6 max-w-sm mx-4 md:max-w-md"
        // ✅ AJOUT : Attributs ARIA pour le modal
        role="dialog"
        aria-modal="true"
        aria-label={t('components.colorPicker.title')}
        tabIndex={-1}
      >
        {/* ✅ MODIFICATION : Heading avec niveau approprié */}
        <h2 className="text-lg font-semibold mb-4">{t('components.colorPicker.title')}</h2>
        
        {/* Layout Mobile : 5 couleurs × 4 tons (vertical) */}
        <div className="mb-4 space-y-2 md:hidden">
          {colorNames.map((colorName) => (
            <div key={colorName} className="grid grid-cols-4 gap-2">
              {COLOR_PALETTE[colorName].map((colorValue, colIndex) => {
                const isSelected = isColorSelected(colorValue);
                return (
                  <button
                    key={`${colorName}-${colIndex}`}
                    className={`w-10 h-10 border-2 hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      isSelected ? 'border-gray-800 ring-2 ring-gray-400' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: colorValue }}
                    onClick={() => {
                      // ✅ SÉCURITÉ : Vérifier que onColorSelect existe et gérer les erreurs
                      if (typeof onColorSelect === 'function') {
                        try {
                          onColorSelect(colorValue);
                        } catch (error) {
                          // Log l'erreur mais ne pas faire planter le composant
                          console.error('Error in onColorSelect callback:', error);
                        }
                      }
                    }}
                    // ✅ AJOUT : Attributs d'accessibilité
                    aria-label={getColorLabel(colorName, colorValue, colIndex)}
                    aria-pressed={isSelected}
                    type="button"
                    tabIndex={0}
                  />
                );
              })}
            </div>
          ))}
        </div>
        
        {/* Layout Desktop : 4 tons × 5 couleurs (horizontal) */}
        <div className="mb-4 space-y-2 hidden md:block">
          {/* Créer 4 lignes (tons) avec 5 colonnes (couleurs) */}
          {[0, 1, 2, 3].map((toneIndex) => (
            <div key={toneIndex} className="grid grid-cols-6 gap-2">
              {colorNames.map((colorName) => {
                const colorValue = COLOR_PALETTE[colorName][toneIndex];
                const isSelected = isColorSelected(colorValue);
                return (
                  <button
                    key={`${colorName}-${toneIndex}`}
                    className={`w-10 h-10 border-2 hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      isSelected ? 'border-gray-800 ring-2 ring-gray-400' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: colorValue }}
                    onClick={() => {
                      // ✅ SÉCURITÉ : Vérifier que onColorSelect existe et gérer les erreurs
                      if (typeof onColorSelect === 'function') {
                        try {
                          onColorSelect(colorValue);
                        } catch (error) {
                          // Log l'erreur mais ne pas faire planter le composant
                          console.error('Error in onColorSelect callback:', error);
                        }
                      }
                    }}
                    // ✅ AJOUT : Attributs d'accessibilité
                    aria-label={getColorLabel(colorName, colorValue, toneIndex)}
                    aria-pressed={isSelected}
                    type="button"
                    tabIndex={0}
                  />
                );
              })}
            </div>
          ))}
        </div>
        
        <button
          onClick={() => {
            // ✅ SÉCURITÉ : Vérifier que onClose existe et gérer les erreurs
            if (typeof onClose === 'function') {
              try {
                onClose();
              } catch (error) {
                // Log l'erreur mais ne pas faire planter le composant
                console.error('Error in onClose callback:', error);
              }
            }
          }} // ✅ MODIFICATION : Wrapper pour éviter l'événement
          className="w-full bg-gray-500 text-white py-2 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          // ✅ AJOUT : Attributs d'accessibilité
          type="button"
          aria-label="Close color picker"
        >
          {t('components.colorPicker.close')}
        </button>
      </div>
    </div>
  );
}